import React, { useState, useEffect, useMemo } from 'react';
import MultiSelect from '../../ClassSearch/MultiSelect';
import ProgramDetailsModal from './ProgramDetailsModal';
import { useAuth } from '../../../contexts/AuthContext';
import { db } from "../../firebase";
import { doc, getDoc } from "firebase/firestore";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faArrowRight, faTimes } from '@fortawesome/free-solid-svg-icons';

const ProgramSearch = ({ onTakeSurvey }) => {
  const { currentUser, fetchedProgramRecs } = useAuth();

  const [programs, setPrograms] = useState([]);
  const [filteredPrograms, setFilteredPrograms] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [facultyFilter, setFacultyFilter] = useState([]);
  const [programTypeFilter, setProgramTypeFilter] = useState([]);
  const [lengthFilter, setLengthFilter] = useState([]);
  const [userSurveyResults, setUserSurveyResults] = useState({});

  // New loading state to prevent premature "No Results Found" display.
  const [loading, setLoading] = useState(true);

  // Modal state for program details and warning modal.
  const [showModal, setShowModal] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [surveyFinished, setSurveyFinished] = useState(true);

  // Helper: Compute a background gradient based on recScore.
  const getBackgroundGradient = (score) => {
    const solidGold = [255, 215, 0];
    const forestGreen = [34, 139, 34];
    const crimson = [220, 20, 60];

    let startColor, endColor, percentage;
    if (score >= 50) {
      startColor = solidGold;
      endColor = forestGreen;
      percentage = (score - 50) / 50;
    } else {
      startColor = crimson;
      endColor = solidGold;
      percentage = score / 50;
    }
    const interpolateColor = (start, end, factor) =>
      start.map((startComponent, index) =>
        Math.round(startComponent + factor * (end[index] - startComponent))
      );
    const [r, g, b] = interpolateColor(startColor, endColor, percentage);
    return `rgb(${r}, ${g}, ${b})`;
  };

  // Fetch user data and calculate recommendation scores.
  useEffect(() => {
    if (fetchedProgramRecs && currentUser) {
      const userDocRef = doc(db, "users", currentUser.uid);
      getDoc(userDocRef)
        .then((docSnap) => {
          if (docSnap.exists()) {
            const userData = docSnap.data();
            let programsWithScore = [];
            if (!userData.careerSurveyFinished) {
              // If the survey isn’t finished, assign a default recScore of "N/A".
              programsWithScore = fetchedProgramRecs.map((program) => ({
                ...program,
                recScore: "N/A",
              }));
              setSurveyFinished(false);
              setShowWarningModal(true);
            } else {
              setSurveyFinished(true);
              const surveyResults = userData.careerSurveyResults || {};
              setUserSurveyResults(surveyResults)
              const dotProducts = fetchedProgramRecs.map((program) => {
                return (surveyResults.Realistic || 0) * (program.Realistic || 0) +
                       (surveyResults.Investigative || 0) * (program.Investigative || 0) +
                       (surveyResults.Artistic || 0) * (program.Artistic || 0) +
                       (surveyResults.Social || 0) * (program.Social || 0) +
                       (surveyResults.Enterprising || 0) * (program.Enterprising || 0) +
                       (surveyResults.Conventional || 0) * (program.Conventional || 0);
              });
              const minDot = Math.min(...dotProducts);
              const maxDot = Math.max(...dotProducts);
              programsWithScore = fetchedProgramRecs.map((program, index) => {
                const dot = dotProducts[index];
                let normalizedScore = 50;
                if (maxDot !== minDot) {
                  normalizedScore = 2 + ((dot - minDot) / (maxDot - minDot)) * (98 - 2);
                }
                const factor = Math.min(program.ProgramName.length, 20) / 20;
                const randomOffset = ((program.PathwayName.length % 5) + ((program.ProgramDescription.length % 5) / 7) - (16 / 7)) * factor;
                const finalScore = Number((normalizedScore + randomOffset).toFixed(2));
                return {
                  ...program,
                  recScore: finalScore,
                };
              });
              // Sort programs AFTER rec scores are calculated.
              programsWithScore.sort((a, b) => {
                if (typeof a.recScore === 'number' && typeof b.recScore === 'number') {
                  return b.recScore - a.recScore;
                }
                return 0;
              });
            }
            setPrograms(programsWithScore);
            setFilteredPrograms(programsWithScore);
            setLoading(false);
          }
        })
        .catch((error) => {
          console.error("Error fetching user data:", error);
          toast.error("Error fetching user data: " + error.message);
          setLoading(false);
        });
    }
  }, [fetchedProgramRecs, currentUser]);

  // Generate filter suggestions.
  const facultySuggestions = useMemo(() => {
    const faculties = new Set();
    programs.forEach((program) => {
      if (program.Faculty) faculties.add(program.Faculty);
    });
    return Array.from(faculties);
  }, [programs]);

  const programTypeSuggestions = useMemo(() => ['Major', 'Minor', 'Certificate'], []);
  const lengthSuggestions = useMemo(() => ['2-Year', '4-Year'], []);

  // Apply filters and sort programs.
  useEffect(() => {
    let filtered = programs;
    if (searchQuery) {
      filtered = filtered.filter((program) =>
        program.ProgramName.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    if (facultyFilter.length > 0) {
      filtered = filtered.filter((program) => facultyFilter.includes(program.Faculty));
    }
    if (programTypeFilter.length > 0) {
      filtered = filtered.filter((program) =>
        programTypeFilter.includes(program.PathwayType)
      );
    }
    if (lengthFilter.length > 0) {
      filtered = filtered.filter((program) => lengthFilter.includes(program.Length));
    }
    // Ensure sorted order is maintained after filtering.
    filtered.sort((a, b) => {
      if (typeof a.recScore === 'number' && typeof b.recScore === 'number') {
        return b.recScore - a.recScore;
      }
      return 0;
    });
    setFilteredPrograms(filtered);
  }, [programs, searchQuery, facultyFilter, programTypeFilter, lengthFilter]);

  const resetFilters = () => {
    setSearchQuery('');
    setFacultyFilter([]);
    setProgramTypeFilter([]);
    setLengthFilter([]);
  };

  // Modal handlers.
  const openModal = (program) => {
    setSelectedProgram(program);
    setShowModal(true);
  };

  const closeModal = () => {
    setSelectedProgram(null);
    setShowModal(false);
  };

  // Warning Modal with updated close button and outside click handler.
  const WarningModal = ({ show, onClose, onTakeSurvey }) => {
    if (!show) return null;
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
        onClick={onClose}
      >
        <div
          className="bg-white rounded-lg shadow-lg p-6 max-w-sm w-full relative"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          >
            <FontAwesomeIcon className="w-5 h-5" icon={faTimes} />
          </button>
          <h2 className="text-xl font-bold mb-4 flex items-center">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 mr-2" />
            Warning
          </h2>
          <p className="mb-4">
            You haven't completed the career survey yet. Please complete it for more accurate recommendation scores.
          </p>
          <button
            onClick={() => { if (onTakeSurvey) onTakeSurvey(); onClose(); }}
            className="bg-blue-500 hover:bg-blue-700 text-white px-4 py-2 rounded flex items-center justify-center"
          >
            Take the survey <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-12 gap-4 p-4">
      {/* Filters Sidebar */}
      <div className="col-span-3 p-4 bg-gray-50 shadow-md border rounded-lg self-start">
        <h5 className="text-lg font-bold text-center mb-4">Filters</h5>
        <div className="mb-3">
          <label htmlFor="searchQuery" className="block text-sm font-medium text-gray-700 mb-1">
            Search:
          </label>
          <input
            type="text"
            id="searchQuery"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="form-input mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          />
        </div>
        <div className="mb-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Faculty:</label>
          <MultiSelect
            suggestions={facultySuggestions}
            value={facultyFilter}
            onChange={(e) => setFacultyFilter(e.target.value)}
            placeholder="Select Faculty"
            required={false}
            unselectable={[]}
            tagColor="green"
          />
        </div>
        <div className="mb-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Program Type:</label>
          <MultiSelect
            suggestions={programTypeSuggestions}
            value={programTypeFilter}
            onChange={(e) => setProgramTypeFilter(e.target.value)}
            placeholder="Select Program Type"
            required={false}
            unselectable={[]}
            tagColor="blue"
          />
        </div>
        <div className="mb-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">Length:</label>
          <MultiSelect
            suggestions={lengthSuggestions}
            value={lengthFilter}
            onChange={(e) => setLengthFilter(e.target.value)}
            placeholder="Select Length"
            required={false}
            unselectable={[]}
            tagColor="purple"
          />
        </div>
        <button
          className="bg-blue-500 hover:bg-blue-700 text-white text-sm py-2 px-4 rounded w-full"
          onClick={resetFilters}
        >
          Reset Filters
        </button>
      </div>

      {/* Programs Listing */}
      <div className="col-span-9">
        <div className="bg-gray-50 border rounded-lg shadow-md p-4 min-h-[70vh]">
          <h5 className="text-lg font-bold text-center mb-4 flex justify-center items-center">
            Recommended Programs
            {!surveyFinished && (
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="text-red-500 hover:text-red-700 w-4 h-4 ml-2 cursor-pointer"
                onClick={() => setShowWarningModal(true)}
              />
            )}
          </h5>
          <div className="space-y-2 overflow-y-auto" style={{ maxHeight: '70vh' }}>
            {loading ? (
              <div className="text-center">Loading...</div>
            ) : (
              filteredPrograms.length === 0 ? (
                <div className="text-center text-red-600">No Results Found</div>
              ) : (
                filteredPrograms.map((program, index) => (
                  <div key={index} className="rounded-lg bg-white border border-gray-200 px-3 py-2">
                    <div className="grid grid-cols-12 gap-4 items-center">
                      {/* Program Name & Faculty */}
                      <div className="col-span-5">
                        <div
                          className="text-md font-semibold truncate"
                          title={program.ProgramName}
                        >
                          {program.ProgramName}
                        </div>
                        <div className="text-sm text-gray-500">{program.Faculty}</div>
                      </div>
                      {/* Credits Display */}
                      <div className="col-span-2 text-left text-gray-700 text-sm">
                        {program.OverallCR} Total Credits 
                      </div>
                      <div className="col-span-2 text-left text-gray-700 text-sm">
                        {program.Length} {program.PathwayType}
                      </div>
                      {/* Rec Score and Details Button */}
                      <div className="col-span-3 flex justify-end items-center space-x-2">
                        {typeof program.recScore === 'number' ? (
                          <span
                            style={{ backgroundColor: getBackgroundGradient(program.recScore) }}
                            className="rounded-md px-2 py-1 text-xs font-medium text-white"
                          >
                            Rec Score: {program.recScore.toFixed(2)}
                          </span>
                        ) : (
                          <span
                            style={{ backgroundColor: "gray" }}
                            className="rounded-md px-2 py-1 text-xs font-medium text-white"
                          >
                            Rec Score: N/A
                          </span>
                        )}
                        <button
                          onClick={() => openModal(program)}
                          className="bg-blue-500 hover:bg-blue-700 text-white text-xs px-3 py-1 rounded"
                        >
                          Details
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <ProgramDetailsModal show={showModal} onClose={closeModal} program={selectedProgram} userSurveyResults={userSurveyResults} />
      <WarningModal
        show={showWarningModal}
        onClose={() => setShowWarningModal(false)}
        onTakeSurvey={onTakeSurvey}
      />
    </div>
  );
};

export default ProgramSearch;
